<script lang="ts">
import { Users, Handshake, Ellipsis } from '@lucide/vue';

export default {
  name: 'TowerCard',
  components: {
    Users,
    Handshake,
    Ellipsis,
  },
};
</script>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { TowerCardData, TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { DateTime } from 'luxon';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import { IsUserABroker, IsUserAnAdmin, IsUserAClient } from '@/utilities/AuthUtils';

const props = defineProps<{
  cardData: TowerCardData;
  optionsOpen?: boolean;
  highlightTowerId?: string;
  selected?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', towerId: string, selected: boolean): void;
  (e: 'edit', towerId: string): void;
  (e: 'rename', towerId: string): void;
  (e: 'duplicate', towerId: string): void;
  (e: 'toggle-publish', towerId: string, published: boolean): void;
  (e: 'delete', towerCard: TowerCardData): void;
  (e: 'update:optionsOpen', optionsOpen: boolean): void;
  (e: 'update:selected', selected: boolean): void;
}>();

const store = useStore<AppState>();

interface TowerTypeChip {
  bgColor: string;
  textColor: string;
  text: string;
}

const towerChips: Record<TowerType, TowerTypeChip> = {
  [TowerType.BoundCoverage]: { bgColor: 'bg-blue-200', textColor: 'text-blue-900', text: 'Bound Coverage' },
  [TowerType.Renewal]: { bgColor: 'bg-red-200', textColor: 'text-red-900', text: 'Renewal' },
  [TowerType.NewCoverage]: { bgColor: 'bg-orange-200', textColor: 'text-orange-900', text: 'New Coverage' },
  [TowerType.Scratch]: { bgColor: 'bg-emerald-200', textColor: 'text-emerald-900', text: 'Scratch' },
  [TowerType.MultiTower]: { bgColor: 'bg-[#d98ad2]', textColor: 'text-white', text: 'MultiTower' },
};

const showOptions = ref(false);
const optionsMenu = ref<HTMLElement>();
const showContributors = ref<boolean>(false);
const cardSelected = ref<boolean>(false);

onBeforeMount(() => {
  cardSelected.value = props.selected ?? false;
});

// ---------
// computed
// ---------
const versionLabel = computed(() => {
  if (props.cardData.versionLabel) {
    if (props.cardData.versionLabel.length > 60) {
      return props.cardData.versionLabel.substring(0, 60).trim() + '...';
    }
    return props.cardData.versionLabel;
  }
  return 'Initial Version';
});

const programName = computed(() => {
  let programName = '';
  if (props.cardData.program) {
    const program = props.cardData.program;
    
    if (program.type === 'Manual') {
      programName = `Manual ${program.year} ${program.name}`;
    } else {
      programName = program.name;
    }

    if (programName) {
      if (programName.length > 50) {
        return programName.substring(0, 50).trim() + '...';
      }
    }
    
    return programName;
  }

  return '';
});

const towerName = computed(() => {
  if (props.cardData.towerName) {
    if (props.cardData.towerName.length > 50) {
      return props.cardData.towerName.substring(0, 50).trim() + '...';
    }
    return props.cardData.towerName;
  }
  return '';
});

const userEmail = computed(() => {
  if (props.cardData.user.userEmail) {
    if (props.cardData.user.userEmail.length > 50) {
      return props.cardData.user.userEmail.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.userEmail;
  }
  return '';
});

const userName = computed(() => {
  if (props.cardData.user.name) {
    if (props.cardData.user.name.length > 50) {
      return props.cardData.user.name.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.name;
  }
  return '';
});

const canDelete = computed(() => {
  if (props.cardData.published) {
    return false;
  }

  if (IsUserAnAdmin(store.state.user)) {
    return true;
  }

  if (IsUserABroker(store.state.user) && store.state.user?.user.id === props.cardData.user.id) {
    return true;
  }

  return false;
});

const updatedOn = computed(() => {
  if (props.cardData.updatedOn) {
    let date = DateTime.fromISO(props.cardData.updatedOn);

    if (date.startOf('day').equals(DateTime.now().startOf('day'))) {
      return date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    return date.toLocaleString(DateTime.DATE_MED) + ' - ' + date.toLocaleString(DateTime.TIME_SIMPLE);
  }
  return '';
});

const isMultiTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.MultiTower;
});

const multiTowerTowerCards = computed(() => {
  if (props.cardData.towerCards) {
    return props.cardData.towerCards;
  }
  return [];
});

const highlightCard = computed(() => {
  return props.highlightTowerId === props.cardData.towerId;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// ---------
// methods
// ---------
function handleCardClick() {
  cardSelected.value = !cardSelected.value;
  emit('click', props.cardData.towerId, cardSelected.value);
}

function handleEditTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('edit', props.cardData.towerId);
}

function handleRenameTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('rename', props.cardData.towerId);
}

function handleDuplicateTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('duplicate', props.cardData.towerId);
}

function handleDeleteTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('delete', props.cardData);
}

function handlePublishTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('toggle-publish', props.cardData.towerId, !props.cardData.published);
}

function toggleOptionsMenu(e: Event) {
  e.stopPropagation();
  showOptions.value = !showOptions.value;
  emit('update:optionsOpen', showOptions.value);

  // set focus on open for blur to work
  if (showOptions.value) {
    setTimeout(() => {
      optionsMenu.value?.focus();
    }, 50);
  }
}

function handleOptionsBlur(e: FocusEvent) {
  if (showOptions.value) {
    emit('update:optionsOpen', false);
    showOptions.value = false;
  }
}
</script>

<template>
  <div class="group group pointer-events-auto relative flex cursor-pointer">
    <div
      @click="handleCardClick"
      class="group pointer-events-auto relative flex h-56 w-80 cursor-pointer flex-col justify-between rounded-md border border-solid px-2 py-2 text-[#133044] shadow-md group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
      :class="{
        'border-2 border-blue-500 bg-blue-50 shadow-none group-hover:border-blue-500': cardSelected,
        'border-gray-300 bg-white': !cardSelected,
        'border-red-500': highlightCard,
      }"
    >
      <div class="flex justify-between">
        <div class="flex gap-2 overflow-hidden px-2">
          <p :title="cardData.versionLabel" class="grow break-words text-base font-medium">
            {{ versionLabel }}
          </p>
        </div>

        <div v-if="!isUserAClient"
          ref="optionsMenu"
          tabindex="0"
          @blur="handleOptionsBlur"
          @click="toggleOptionsMenu"
          class="z-10 flex h-8 w-9 cursor-pointer place-content-center overflow-hidden rounded-md bg-white hover:bg-gray-100"
        >
          <Ellipsis class="m-auto text-gray-400" />

          <div v-if="showOptions" class="absolute right-2 top-11">
            <ul class="menu rounded-lg border border-solid border-gray-200 bg-white shadow-xs">
              <li>
                <a @click="handleEditTower" class="text-black">Edit</a>
                <a @click="handleRenameTower" class="text-black">Rename</a>
                <a @click="handleDuplicateTower" class="text-black">Duplicate</a>
                <a @click="handlePublishTower" class="text-black">{{
                  cardData.published ? 'Unpublish' : 'Publish to Clients'
                }}</a>
                <a v-if="canDelete" @click="handleDeleteTower" class="text-primary">Delete</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="bg align-center flex h-6 w-fit justify-center rounded-lg px-3"
        :class="
          towerChips[cardData.towerTypeId as TowerType].bgColor +
          ' ' +
          towerChips[cardData.towerTypeId as TowerType].textColor
        "
      >
        <span class="my-auto">{{ towerChips[cardData.towerTypeId as TowerType].text }}</span>
      </div>

      <div class="my-4 ml-2">
        <div
          v-if="isMultiTower"
          class="flex w-fit flex-col gap-1 rounded border border-solid border-gray-200 p-1 text-xs text-gray-600"
        >
          <div v-for="towerCard in multiTowerTowerCards" :key="towerCard.towerId" class="flex gap-1">
            <span class="font-bold">{{ towerCard.versionLabel }}</span>
            <span v-if="towerCard.towerTypeId === TowerType.MultiTower" class="italic text-[#d98ad2]"
              >(MultiTower)</span
            >
            <span v-else class="italic"
              >({{
                towerCard.program && towerCard.program.name !== '' ? towerCard.program.name : towerCard.towerName
              }})</span
            >
          </div>
        </div>
        <div v-else class="text-base text-gray-600">
          {{ programName && programName !== '' ? programName : towerName }}
        </div>
      </div>

      <div
        class="mt-auto flex w-full flex-row items-end justify-between gap-2 px-1 text-end text-xs"
      >
        <div
          v-if="cardData.published && !isUserAClient"
          class="tooltip tooltip-top z-20 flex justify-end"
          data-tip="Published to Clients"
        >
          <Users class="self-end text-green-500" :size="20" />
        </div>

        <div
          v-if="props.cardData.contributors && props.cardData.contributors.length > 0"
          class="relative flex justify-end"
          data-testid="tower-card-contributor-icon"
        >
          <Handshake
            :size="20"
            class="self-end text-gray-400"
            @mouseover="showContributors = true"
            @mouseleave="showContributors = false"
          />
          <div
            v-show="showContributors"
            class="absolute left-0 top-5 z-50 mt-1 whitespace-nowrap rounded-md border border-gray-300 bg-white px-4 py-2 text-start shadow-md"
          >
            <p class="mb-2 text-sm font-bold">Contributors</p>
            <ul class="m-0 list-none p-0">
              <li
                v-for="(contributor, index) in props.cardData.contributors.split(',')"
                :key="index"
                class="text-sm"
              >
                {{ contributor }}
              </li>
            </ul>
          </div>
        </div>

        <div class="ml-auto mr-0 flex flex-col">
          <p v-if="!isUserAClient" class="mb-0 mt-auto break-all">{{ userName !== '' ? userName : userEmail }}</p>
          <p class="font mb-0 text-gray-500">Updated {{ updatedOn }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
